import { computed, ref, shallowRef } from "vue"
import { defineStore } from "pinia"
import { buildDirectory, type User } from "@auger/core"
import { fetchDirectory, fetchPeopleByIds } from "@/common/hasura/users"
import type { FetchPeopleByIdsQuery } from "@/gql/graphql"
import { client } from "@/init/apollo"

// The staff directory (@auger/core directory.ts): one entry per person on the
// staff list, keyed by the PERSON record id. `record_id` repeats the id and
// `record` is the person record (avatar, name).
//
// A person who is NOT on the staff list (a former owner, assignee or actor)
// still has a name. `getUserById` resolves them from `others`, a cache filled
// by one batched query per tick; such an entry has `former: true` and is never
// in `users`, so no picker offers it.

/** Resolve a person id to a directory entry, or a former person's entry. For display. */
export const getUserById = (personId: any) => useUsersStore().resolve(personId)
export const getActiveUsers = () => useUsersStore().activeUsers
export const getPartners = () => useUsersStore().partners

/** A directory entry with the person record it came from (name, avatar). */
export type DirectoryEntry = User & { record: FetchPeopleByIdsQuery["people"][number] }

export const useUsersStore = defineStore("users", () => {
  // Entries carry the whole person record (`record`) for avatars.
  const users = ref<DirectoryEntry[]>([])
  const loading = ref(false)
  const initialized = ref(false)
  // Former people by person id; null = asked and not a readable person.
  const others = shallowRef(new Map<number, DirectoryEntry | null>())

  const partners = computed(() => users.value.filter((u) => u.partner))
  // Everyone on the staff list is active: the list holds active members only.
  const activeUsers = computed(() => users.value)

  const getByRecordId = (recordId: any) => users.value.find((u) => u.id === recordId) || null

  // Ids to load in the next batch, and the ids already asked for.
  let queued = new Set<number>()
  const asked = new Set<number>()
  let flushing: Promise<void> | null = null

  const flush = () => {
    const ids = [...queued]
    queued = new Set()
    flushing = null
    if (!ids.length) return Promise.resolve()
    return client
      .query({ query: fetchPeopleByIds, variables: { ids }, fetchPolicy: "network-only" })
      .then(({ data }) => {
        const next = new Map(others.value)
        for (const id of ids) next.set(id, null)
        for (const person of data.people ?? []) {
          next.set(person.id, {
            id: person.id,
            record_id: person.id,
            name: person.name ?? null,
            slug: person.slug ?? null,
            email: null,
            partner: false,
            active: false,
            former: true,
            record: { ...person },
          })
        }
        others.value = next
      })
      .catch((error) => {
        // Let a later render ask again.
        for (const id of ids) asked.delete(id)
        console.error("Failed to load people outside the staff directory", error)
      })
  }

  /** The directory entry for a person, else the former person's entry once
   *  loaded. A miss after the directory has loaded queues the id for the next
   *  batch and reads null until it arrives. */
  const resolve = (personId: any) => {
    if (personId == null) return null
    const id = Number(personId)
    const staff = users.value.find((u) => u.id === id)
    if (staff) return staff
    const other = others.value.get(id)
    if (other !== undefined) return other
    if (initialized.value && Number.isInteger(id) && id > 0 && !asked.has(id)) {
      asked.add(id)
      queued.add(id)
      flushing ??= Promise.resolve().then(flush)
    }
    return null
  }

  /** Load the directory. Memoized on `initialized`; pass `force` to refetch. */
  const fetch = (force = false) => {
    if (initialized.value && !force) return Promise.resolve(users.value)
    loading.value = true
    return client
      .query({ query: fetchDirectory, fetchPolicy: "network-only" })
      .then(({ data }) => {
        users.value = buildDirectory(
          {
            staff: data.directory_staff,
            partners: data.directory_partners,
          },
          (person) => ({ record: { ...person } })
        )
        initialized.value = true
        return users.value
      })
      .finally(() => {
        loading.value = false
      })
  }

  /** Test seam: wait for a queued batch. */
  const settled = () => flushing ?? Promise.resolve()

  return {
    users,
    loading,
    partners,
    activeUsers,
    others,
    getByRecordId,
    resolve,
    fetch,
    settled,
  }
})
